/**
 * M28 task-05 (ADM-12/13; COP-117 D-14) — the SINGLE documents master
 * component. A server-side filtered + paginated master list over the
 * `documents` resource (GET /documents, mirrors /relations), a status/track/
 * owner filter toolbar, and a hybrid-search box calling POST /documents/search
 * directly (a page action, not a CRUD verb — relations/db-connectors Create
 * precedent). Mounted at /admin/documents here; task-06 mounts THIS SAME source
 * at /me/documents (D-14 — a second listing would be a scope error). Nothing
 * branches on role inside the component; an admin simply receives more rows
 * because the hub short-circuits.
 *
 * Admin test convention (prep §9): columns + toolbar are module-level exported
 * seams (`buildDocumentColumns`, `DocumentsFilterToolbar`) rendered through a
 * raw `useReactTable` harness / `renderToStaticMarkup` — no jsdom.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import type { CrudFilters } from '@refinedev/core';
import { Link, useLocation } from 'react-router';
import {
  DOCUMENT_STATUS,
  type DocumentListItem,
  type DocumentPipelineSettings,
  type DocumentStatus,
  type DocumentTrackEntity,
  type SearchResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/DataTable';
import { fmtDateTime, truncateId } from '@/lib/format';
import { hub } from '@/lib/hub-client';
import { filterValue, isUuid } from '@/lib/list-filters';

const STATUS_VALUES = Object.values(DOCUMENT_STATUS) as DocumentStatus[];

const STATUS_VARIANT: Record<DocumentStatus, 'success' | 'destructive' | 'secondary' | 'muted'> = {
  queued: 'muted',
  extracting: 'secondary',
  chunking: 'secondary',
  embedding: 'secondary',
  embedding_failed: 'destructive',
  ready: 'success',
  failed: 'destructive',
};

export function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status]} className="rounded-full">
      {status}
    </Badge>
  );
}

/** The human "where does this document live" label from its track entity. */
export function trackEntityLabel(entity: DocumentTrackEntity): string {
  switch (entity.kind) {
    case 'track':
      return entity.track;
    case 'global':
      return 'Global';
  }
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Column factory (exported test seam). No callbacks — navigation is link-based.
 * `showBase` is the detail route prefix: the SAME component mounts under
 * /admin/documents (admins) and /me/documents (everyone), so the row link must
 * follow the active shell (task-06; COP-117 D-14). Defaults to the admin base so
 * existing behavior is byte-identical.
 */
export function buildDocumentColumns(
  showBase = '/admin/documents/show',
  ownerLabel: (userId: string) => string | undefined = () => undefined,
): ColumnDef<DocumentListItem>[] {
  return [
    {
      id: 'filename',
      header: 'Name',
      accessorKey: 'filename',
      cell: (c) => (
        <Link
          to={`${showBase}/${c.row.original.id}`}
          className="font-medium underline-offset-2 hover:underline"
        >
          {c.row.original.filename}
        </Link>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (c) => <DocumentStatusBadge status={c.getValue() as DocumentStatus} />,
    },
    {
      id: 'trackEntity',
      header: 'Location',
      accessorKey: 'trackEntity',
      cell: (c) => (
        <span className="text-sm">{trackEntityLabel(c.getValue() as DocumentTrackEntity)}</span>
      ),
    },
    {
      id: 'uploaderUserId',
      header: 'Owner',
      accessorKey: 'uploaderUserId',
      // An email says who; a truncated uuid says nothing and cannot even be
      // copied into the owner filter. `ownerLabel` is read at RENDER time so the
      // column array stays referentially stable while the user list loads
      // (see the useMemo at the call site).
      cell: (c) => {
        const userId = c.getValue() as string;
        const email = ownerLabel(userId);
        return email ? (
          <span className="text-sm" title={userId}>
            {email}
          </span>
        ) : (
          <code className="font-mono text-xs text-muted-foreground">{truncateId(userId)}</code>
        );
      },
    },
    {
      id: 'sizeBytes',
      header: 'Size',
      accessorKey: 'sizeBytes',
      cell: (c) => <span className="tabular-nums">{fmtBytes(c.getValue() as number)}</span>,
    },
    {
      id: 'createdAt',
      header: 'Uploaded',
      accessorKey: 'createdAt',
      cell: (c) => (
        <span className="tabular-nums text-muted-foreground">{fmtDateTime(c.getValue() as string)}</span>
      ),
    },
  ];
}

export interface DocumentsFilterToolbarProps {
  tracks: { id: string; label: string }[];
  /** Selectable owners. Empty (e.g. a non-admin, who cannot list users) → free text. */
  owners: { id: string; email: string }[];
  status: string;
  setStatus: (value: string) => void;
  trackId: string;
  setTrackId: (value: string) => void;
  ownerUserId: string;
  setOwnerUserId: (value: string) => void;
  filename: string;
  setFilename: (value: string) => void;
}

/** Props-only filter toolbar (exported test seam). */
export function DocumentsFilterToolbar({
  tracks,
  owners,
  status,
  setStatus,
  trackId,
  setTrackId,
  ownerUserId,
  setOwnerUserId,
  filename,
  setFilename,
}: DocumentsFilterToolbarProps) {
  const selectClass =
    'flex h-8 rounded-md border border-input bg-transparent px-2 text-sm shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
  return (
    <>
      {/* Narrows THIS listing by document name. Not the hybrid-search panel
          above, which ranks document CONTENT across the whole corpus. */}
      <Input
        aria-label="Document name"
        placeholder="Filter by name…"
        className="h-8 w-56 shadow-none"
        value={filename}
        onChange={(e) => setFilename(e.target.value)}
      />
      <select
        aria-label="Status"
        className={selectClass}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        <option value="">All statuses</option>
        {STATUS_VALUES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {tracks.length > 0 ? (
        <select
          aria-label="Track"
          className={selectClass}
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
        >
          <option value="">All tracks</option>
          {tracks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      ) : (
        // Tracks unavailable (fetch failed) — free text, relations toolbar idiom.
        <Input
          aria-label="Track"
          placeholder="Track id"
          className="h-8 w-44 shadow-none"
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
        />
      )}
      {owners.length > 0 ? (
        <Combobox
          aria-label="Owner"
          className="w-64"
          value={ownerUserId}
          valueLabel={owners.find((o) => o.id === ownerUserId)?.email}
          options={ownerOptions(owners)}
          onSelect={setOwnerUserId}
          placeholder="All owners"
          searchPlaceholder="Search by email…"
        />
      ) : (
        // Users unavailable (a non-admin cannot list them) — free text, the
        // same fallback the track filter uses.
        <Input
          aria-label="Owner user id"
          placeholder="Owner user id"
          className="h-8 w-64 shadow-none"
          value={ownerUserId}
          onChange={(e) => setOwnerUserId(e.target.value)}
        />
      )}
    </>
  );
}

/** "All owners" first so clearing the filter is a pick, not a text deletion. */
function ownerOptions(owners: { id: string; email: string }[]): ComboboxOption[] {
  return [
    { value: '', label: 'All owners' },
    ...owners.map((owner) => ({ value: owner.id, label: owner.email })),
  ];
}

type SearchHit = SearchResponse['results'][number];

/** Cross-track hybrid-search box + fused hits (exported test seam). */
export function DocumentsSearchPanel({
  query,
  setQuery,
  onSearch,
  results,
  searching,
  error,
  hasSearched,
  showBase = '/admin/documents/show',
}: {
  query: string;
  setQuery: (value: string) => void;
  onSearch: () => void;
  results: SearchHit[];
  searching: boolean;
  error: string | null;
  hasSearched: boolean;
  showBase?: string;
}) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Search documents"
          placeholder="Search across every document you can read…"
          className="h-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSearch();
          }}
        />
        <Button onClick={onSearch} disabled={searching || !query.trim()}>
          {searching ? 'Searching…' : 'Search'}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {hasSearched && !searching && !error && results.length === 0 && (
        <p className="text-sm text-muted-foreground">No matches.</p>
      )}
      {results.length > 0 && (
        <ul className="divide-y">
          {results.map((hit) => (
            <li key={hit.chunkId} className="py-2">
              <Link
                to={`${showBase}/${hit.documentId}`}
                className="text-sm font-medium underline-offset-2 hover:underline"
              >
                {hit.filename}
              </Link>
              <p className="mt-0.5 line-clamp-3 text-sm text-muted-foreground">{hit.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Milliseconds as a readable duration — the two Textract knobs are ms in the
 * wire shape but read as "3s" / "5m" on screen. */
function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${Number(seconds.toFixed(1))} s`;
  return `${Number((seconds / 60).toFixed(1))} min`;
}

/**
 * Read-only RAG pipeline settings (ADM-06; issue #212 "at minimum read-only").
 * Pure/props-only seam, same shape as the Identity Providers page's
 * `IdentityConfigPanel`. Renders nothing without settings — the route is
 * `requireAdmin`, so a non-admin under /me/documents simply never gets a value.
 */
export function PipelineSettingsCard({ settings }: { settings: DocumentPipelineSettings | null }) {
  if (!settings) return null;
  const rows: [string, string][] = [
    ['Chunk size', `${settings.chunkSize} tokens`],
    ['Embedding model', settings.embeddingModelId],
    ['Embedding dimensions', String(settings.embeddingDim)],
    ['Parser provider', settings.parserProvider],
    ['Textract poll interval', fmtMs(settings.textractPollIntervalMs)],
    ['Textract max wait', fmtMs(settings.textractMaxWaitMs)],
    ['Hybrid ranking RRF k', String(settings.rrfK)],
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Document pipeline (RAG)</CardTitle>
        <CardDescription>
          Read-only snapshot of the ingestion, embedding and ranking knobs. Every value is hub
          environment configuration — change it at deploy time.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <dt className="text-xs text-muted-foreground">{k}</dt>
              <dd className="break-all text-sm">{v}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

export interface DocumentsFilterState {
  status: string;
  trackId: string;
  ownerUserId: string;
  filename: string;
}

/**
 * Toolbar state ⇄ refine filters. Both directions are needed: the URL is the
 * only place the toolbar survives a reload (list-filters.test).
 *
 * The id filters are emitted ONLY once their value is a complete uuid — the
 * hub rejects a partial one and refine then leaves the previous, unrelated
 * rows on screen as if they were the filtered result (`isUuid`).
 */
export function documentsCrudFilters({
  status,
  trackId,
  ownerUserId,
  filename,
}: DocumentsFilterState): CrudFilters {
  const filters: CrudFilters = [];
  if (status) filters.push({ field: 'status', operator: 'eq', value: status });
  if (isUuid(trackId)) filters.push({ field: 'trackId', operator: 'eq', value: trackId.trim() });
  if (isUuid(ownerUserId))
    filters.push({ field: 'ownerUserId', operator: 'eq', value: ownerUserId.trim() });
  // A name search is a substring, not an id — no uuid gate.
  if (filename.trim())
    filters.push({ field: 'filename', operator: 'contains', value: filename.trim() });
  return filters;
}

export function documentsFilterState(filters: CrudFilters | undefined): DocumentsFilterState {
  return {
    status: filterValue(filters, 'status'),
    trackId: filterValue(filters, 'trackId'),
    ownerUserId: filterValue(filters, 'ownerUserId'),
    filename: filterValue(filters, 'filename'),
  };
}

export function DocumentsList() {
  // Track select options, seeded from /tracks; empty on fetch failure →
  // free-text fallback (relations/List namespace idiom).
  const [tracks, setTracks] = useState<{ id: string; label: string }[]>([]);
  // Owner options + the id→email map the Owner column reads. GET /users is
  // admin-only, so under /me/documents this stays empty and both the filter and
  // the column fall back to ids — no new exposure, same fallback idiom.
  const [owners, setOwners] = useState<{ id: string; email: string }[]>([]);
  const ownerEmailById = useRef(new Map<string, string>());

  // --- hybrid search (direct hub call; not a refine resource) ---
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Read-only pipeline knobs (ADM-06). The route is admin-only, so under
  // /me/documents this stays null and the card does not render — the same
  // fetch-and-fall-back idiom the owner/track options use.
  const [pipelineSettings, setPipelineSettings] = useState<DocumentPipelineSettings | null>(null);

  useEffect(() => {
    hub
      .tracksList()
      .then((rows) =>
        setTracks(
          rows.map((t) => ({
            id: t.id,
            label: (t.displayName?.trim() || t.name) ?? t.id,
          })),
        ),
      )
      .catch(() => {
        // Hub unreachable — leave empty, the toolbar falls back to free text.
      });
  }, []);

  useEffect(() => {
    hub
      .usersList()
      .then((res) => {
        const rows = res.users.map((u) => ({ id: u.id, email: u.email }));
        // The ref is what the (referentially stable) Owner column reads at
        // render time; the state is what re-renders the table once it lands.
        ownerEmailById.current = new Map(rows.map((r) => [r.id, r.email]));
        setOwners(rows.sort((a, b) => a.email.localeCompare(b.email)));
      })
      .catch(() => {
        // Not an admin (403) or hub unreachable — ids it is.
      });
  }, []);

  useEffect(() => {
    hub
      .documentsPipelineSettings()
      .then(setPipelineSettings)
      .catch(() => {
        // Not an admin (403) or hub unreachable — the card stays hidden.
      });
  }, []);

  // Detail links follow the active shell: this same component mounts at
  // /admin/documents (admins) and /me/documents (everyone) — D-14.
  const { pathname } = useLocation();
  const showBase = pathname.startsWith('/me/') ? '/me/documents/show' : '/admin/documents/show';

  // `ownerEmailById` is a REF, so the column array keeps its identity while the
  // user list loads — a new array here would re-run `useTable`'s filter-sync
  // effect and wipe the toolbar's filters (list-columns-stability.test).
  const columns = useMemo(
    () => buildDocumentColumns(showBase, (userId) => ownerEmailById.current.get(userId)),
    [showBase],
  );
  // `resource` MUST be explicit (same reason as Show.tsx): refine infers it by
  // matching the path against the registered resource routes, and `documents`
  // registers only `list: /admin/documents`. Under /me/documents the match
  // fails, `useTable` warns "`resource` is not defined" and never calls the data
  // provider — an empty table for every user, not an authz outcome (D-14).
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<DocumentListItem>({ columns, refineCoreProps: { resource: 'documents' } });
  // Seeded from the filters refine restored from the URL — otherwise the
  // effect below fires on mount with empty values and erases them.
  const [state, setState] = useState<DocumentsFilterState>(() => documentsFilterState(filters));
  const { status, trackId, ownerUserId, filename } = state;

  // Debounced server-side filters (relations/List idiom) — `eq` filters the
  // data provider maps onto DocumentListQuery params.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(documentsCrudFilters(state), 'replace');
    }, 250);
    return () => clearTimeout(handle);
  }, [state, setFilters]);

  const runSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    setHasSearched(true);
    try {
      const res: SearchResponse = await hub.documentsSearch({ body: { query: q } });
      setResults(res.results);
    } catch (err) {
      setSearchError((err as Error).message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
        <p className="text-sm text-muted-foreground">
          Every document you can read — across tracks and the global bucket — with hybrid search.
        </p>
      </div>
      <DocumentsSearchPanel
        query={searchQuery}
        setQuery={setSearchQuery}
        onSearch={() => void runSearch()}
        results={results}
        searching={searching}
        error={searchError}
        hasSearched={hasSearched}
        showBase={showBase}
      />
      <DataTable
        table={table}
        emptyMessage="No documents."
        toolbar={
          <DocumentsFilterToolbar
            tracks={tracks}
            status={status}
            setStatus={(next) => setState((s) => ({ ...s, status: next }))}
            trackId={trackId}
            setTrackId={(next) => setState((s) => ({ ...s, trackId: next }))}
            ownerUserId={ownerUserId}
            setOwnerUserId={(next) => setState((s) => ({ ...s, ownerUserId: next }))}
            owners={owners}
            filename={filename}
            setFilename={(next) => setState((s) => ({ ...s, filename: next }))}
          />
        }
      />
      <PipelineSettingsCard settings={pipelineSettings} />
    </div>
  );
}
